<template>
  <button
    type="button"
    :class="[
      $style.cell,
      {
        [$style.otherMonth]: !cell.inCurrentMonth,
        [$style.disabled]: cell.disabled,
        [$style.readonly]: readonly,
        [$style.weekend]: cell.isWeekend && cell.inCurrentMonth && !isSelectedLike,
      },
    ]"
    :disabled="cell.disabled"
    :aria-pressed="state.selected || state.rangeStart || state.rangeEnd"
    :aria-current="cell.isToday ? 'date' : undefined"
    :tabindex="cell.inCurrentMonth && !cell.disabled ? 0 : -1"
    :test-id="testId"
    @click="$emit('select', cell.date)"
    @mouseenter="$emit('hover', cell.date)"
    @focus="$emit('hover', cell.date)"
  >
    <span
      ref="circleRef"
      :class="[
        $style.circle,
        {
          [$style.selected]: state.selected || state.rangeStart || state.rangeEnd,
          [$style.today]: cell.isToday && !isSelectedLike,
          [$style.flash]: state.flash,
          [$style.previewEndpoint]: (state.previewStart || state.previewEnd) && !isSelectedLike,
        },
      ]"
    >
      {{ cell.day }}
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import type { DayCell, DaySelectionState } from "./datetimePickerTypes";

const props = withDefaults(
  defineProps<{
    cell: DayCell;
    state: DaySelectionState;
    isRowStart?: boolean;
    isRowEnd?: boolean;
    readonly?: boolean;
    testId?: string;
  }>(),
  {
    isRowStart: false,
    isRowEnd: false,
    readonly: false,
    testId: undefined,
  },
);

defineEmits<{
  select: [date: Date];
  hover: [date: Date];
}>();

const isSelectedLike = computed(() => props.state.selected || props.state.rangeStart || props.state.rangeEnd);

const circleRef = ref<HTMLElement | null>(null);

watch(
  () => props.state.flash,
  (flash) => {
    const el = circleRef.value;
    if (!flash || !el) return;
    el.style.animation = "none";
    void el.offsetWidth;
    el.style.animation = "";
  },
  { flush: "post" },
);
</script>

<style module lang="scss">
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--sca-dp-cell-size, 40px);
  height: var(--sca-dp-cell-size, 40px);
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.circle {
  @include FigmaTypoMixins.typo-body-medium;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--sca-dp-circle-size, 32px);
  height: var(--sca-dp-circle-size, 32px);
  border-radius: var(--border-radius-full, 9999px);
  color: var(--color-content-primary, #272e35);
  cursor: inherit;
  transition:
    background-color 0.12s ease,
    color 0.12s ease,
    box-shadow 0.12s ease;
}

.cell:hover:not(.disabled):not(.readonly) .circle:not(.selected):not(.previewEndpoint) {
  background-color: var(--color-background-neutral-muted, #dee3e7);
}

.cell.readonly {
  cursor: default;
}

.cell:focus-visible {
  outline: none;
}
.cell:focus-visible .circle:not(.selected) {
  box-shadow: 0 0 0 2px var(--color-interaction-border-normal, #8db0fb);
}

.circle.today {
  box-shadow: inset 0 0 0 1px var(--color-border-informative-subtle, #cdddff);
  background-color: var(--color-background-informative-subtle);
}

.circle.flash {
  animation: day-flash 1.2s ease-out;
}

@keyframes day-flash {
  0%,
  25% {
    box-shadow: inset 0 0 0 1px var(--color-background-informative-strong, #1d7c4d);
  }
  100% {
    box-shadow: inset 0 0 0 1px var(--color-border-informative-subtle, #cdddff);
  }
}

.circle.selected {
  background-color: var(--color-border-brand-strong, #3062d4);
  color: var(--color-content-primary-inverted, #fff);
}

.otherMonth .circle {
  color: var(--color-content-disabled, #8896a6);
}

.weekend .circle {
  color: var(--color-content-secondary, #4a545e);
}

.disabled {
  cursor: not-allowed;
}
.disabled .circle {
  color: var(--color-content-disabled, #8896a6);
  opacity: 0.5;
}

@media (max-width: 768px) {
  .cell {
    flex: 1 1 0;
    width: auto;
    min-width: 32px;
  }
}
</style>
